import { useEffect, useMemo, useState } from "react";
import OrganizationService from "../service/organization.service";
import { TierPlan } from "../models/Organization";
import { GoalLock } from "../models/GoalSetting";
import { ApiError } from "../api/ApiError";

/**
 * plan: undefined = request in flight, null = request failed (page behaves as before limits existed).
 * goalCount: undefined = goal list not back yet.
 */
export const deriveGoalLock = (
  plan: TierPlan | null | undefined,
  goalCount: number | undefined,
): GoalLock => {
  const used = goalCount ?? 0;
  if (plan === undefined) {
    return { state: "loading", used, limit: null, planName: "" };
  }
  if (plan === null) {
    return { state: "open", used, limit: null, planName: "" };
  }
  const limit = plan.limits.maxGoals;
  const base = { used, limit, planName: plan.name };
  if (limit === null) return { ...base, state: "open" };
  if (limit === 0) return { ...base, state: "locked" };
  if (goalCount !== undefined && goalCount >= limit) {
    return { ...base, state: "full" };
  }
  return { ...base, state: "open" };
};

export const isAddGoalDisabled = (lock: GoalLock) =>
  lock.state === "locked" || lock.state === "full";

// a plan without goal setting can't use the emissions the prompt would send the user to add
export const promptsForEmissions = (hasEmission: boolean, lock: GoalLock) =>
  !hasEmission && lock.state !== "locked";

export const isGoalCapRefusal = (error: unknown) =>
  error instanceof ApiError && error.code === "FORBIDDEN_GOAL_SETTING_ERROR";

// One plan request per mount; both goal providers call it with their own live goal count.
export const useGoalLimit = (goalCount: number | undefined): GoalLock => {
  const [plan, setPlan] = useState<TierPlan | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    OrganizationService.getTierPlan().then((result) => {
      if (mounted) setPlan(result);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return useMemo(() => deriveGoalLock(plan, goalCount), [plan, goalCount]);
};
